import React, { useState, useEffect } from 'react';
import { addWeight, getWeightProgress } from '../../utils/api';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Box, Modal, Button, Typography, TextField } from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; // Coin icon

const WeightTracker = ({ userId }) => {
    const [weight, setWeight] = useState(70); // Default weight
    const [height, setHeight] = useState(); // Default height for BMI calculation
    const [bmi, setBmi] = useState(null);
    const [bmiStatus, setBmiStatus] = useState('');
    const [progress, setProgress] = useState([]);
    const [timeFrame, setTimeFrame] = useState('yearly'); // default to yearly
    const scaleMarker = 10; // Interval for marker numbers
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);

    const [showPopup, setShowPopup] = useState(false);//popup
    const [isHeightModalOpen, setIsHeightModalOpen] = useState(false);

    useEffect(() => {
        const calculateBmi = () => {
            if (height > 0 && weight > 0) {
                const calculatedBmi = (weight / ((height / 100) ** 2)).toFixed(1);
                setBmi(calculatedBmi);

                if (calculatedBmi < 18.5) setBmiStatus('בגרעון משקל');
                else if (calculatedBmi < 24.9) setBmiStatus('משקל תקין');
                else if (calculatedBmi < 29.9) setBmiStatus('בעודף משקל');
                else setBmiStatus('בעודף משקל, קצת יותר');
            }
        };
        calculateBmi();
    }, [weight, height]);

    useEffect(() => {
        const fetchProgress = async () => {
            if (!userId) return; // Ensure userId exists
            try {
                const data = await getWeightProgress(userId);
                setProgress(data);
            } catch (error) {
                console.error('Error fetching weight progress:', error);
            }
        };
        fetchProgress();
    }, [userId]);

    const handleSave = async () => {
        if (!userId) {
            console.error('No user ID available. Cannot save weight.');
            return;
        }
        try {
            await addWeight(userId, weight);
            const updatedProgress = await getWeightProgress(userId);
            setProgress(updatedProgress);
            setShowPopup(true); // popup
            // window.location.reload(); // Reload the page to reflect changes
        } catch (error) {
            console.error('Error saving weight:', error);
        }
    };


    const aggregatedProgress = React.useMemo(() => {
        if (!progress.length) return [];

        const groupedData = progress.reduce((acc, entry) => {
            const date = new Date(entry.date);
            let key = '';

            if (timeFrame === 'daily') {
                key = date.toLocaleDateString(); // Each day as a separate key
            } else if (timeFrame === 'monthly') {
                key = `${date.getFullYear()}-${date.getMonth() + 1}`;
            } else if (timeFrame === 'yearly') {
                key = `${date.getFullYear()}`;
            }

            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(entry);
            return acc;
        }, {});

        return Object.keys(groupedData)
            .sort((a, b) => new Date(groupedData[a][0].date) - new Date(groupedData[b][0].date))
            .map((key) => {
                const entries = groupedData[key];
                const avgWeight = entries.reduce((sum, e) => sum + e.weight, 0) / entries.length;
                const representativeDate = new Date(
                    Math.max(...entries.map((e) => new Date(e.date)))
                );
                return { date: representativeDate, weight: avgWeight, key };
            });
    }, [progress, timeFrame]);


    const data = React.useMemo(() => ({
        labels: aggregatedProgress.map((entry) => {
            // Use the key as label for weekly, monthly, or yearly data.
            if (timeFrame === 'weekly' || timeFrame === 'monthly' || timeFrame === 'yearly') {
                return entry.key;
            }
            return new Date(entry.date).toLocaleDateString();
        }),
        datasets: [
            {
                label: 'Weight (kg)',
                data: aggregatedProgress.map((entry) => entry.weight),
                borderColor: '#C4A484',
                backgroundColor: 'rgba(0,123,255,0.1)',
                pointBackgroundColor: '#C4A484',
                pointRadius: 4,
            },
        ],
    }), [aggregatedProgress, timeFrame]);

    const activeStyle = {
        cursor: 'pointer',
        fontWeight: 'bold',
        color: '#d5011e', // A highlight color when selected
        padding: '10px 15px',
        borderRadius: '5px',
        backgroundColor: '#f1f1f1', // Background color when selected
    };

    const inactiveStyle = {
        cursor: 'pointer',
        padding: '10px 15px',
        color: '#000', // Default color for unselected
        border: 'none',
        borderRadius: '5px',
        textDecoration: 'none',
    };
    // Move the state variables to the parent component

    const renderScale = () => {
        const scaleRange = Array.from({ length: 201 }, (_, i) => i + 30); // From 30 to 230
        const visibleRange = 25; // Number of lines visible around the central marker

        const handleDragStart = (e) => {
            setIsDragging(true);
            setStartX(e.clientX || e.touches[0].clientX);
        };

        const handleDragMove = (e) => {
            if (!isDragging) return;
            const currentX = e.clientX || e.touches[0].clientX;
            const deltaX = currentX - startX;
            const deltaWeight = Math.round(deltaX / 10); // Adjust 10px per weight unit
            const newWeight = Math.min(
                Math.max(weight + deltaWeight, scaleRange[0]),
                scaleRange[scaleRange.length - 1]
            );
            if (newWeight !== weight) {
                setWeight(newWeight);
                setStartX(currentX); // Reset start position for smoother adjustment
            }
        };

        const handleDragEnd = () => {
            setIsDragging(false);
        };


        return (
            <div
                style={{ position: 'relative', width: '100%', height: '100px', overflow: 'hidden' }}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
            >
                <div
                    style={{
                        display: 'flex',
                        position: 'absolute',
                        left: `calc(50% - ${scaleRange.indexOf(weight) * 20}px)`,
                        transition: isDragging ? 'none' : 'left 0.3s ease-out',
                    }}
                >
                    {scaleRange.map((val) => (
                        <div
                            key={val}
                            style={{
                                width: '10px',
                                height: val % 5 === 0 ? '40px' : '20px',
                                backgroundColor: val === weight ? '#d5011e' : '#000',
                                margin: '0 5px',
                                opacity: Math.abs(weight - val) > visibleRange ? 0 : 1,
                            }}
                        />
                    ))}
                </div>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '2px',
                        height: '100px',
                        backgroundColor: '#d5011e',
                    }}
                />
            </div>
        );
    };

    const handleHeightModal = () => {
        setIsHeightModalOpen(true);
    }


    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            maxWidth: '100%',
            margin: '0 auto',
            backgroundColor: '#f8f9fa',
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            maxWidth: { xs: '95%', sm: '90%', md: '80%', lg: '70%', xl: '60%' },

        }}>
            <Button textAlign="right" justifyContent="flex-start" onClick={handleHeightModal}>
                הזן את הגובה שלך
            </Button>

            <Box width="100%" maxWidth={{ xs: '95%', sm: '90%', md: '80%', lg: '70%', xl: '60%' }}
            >
                <div
                    width="100%"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '20px',
                        borderRadius: '10px',
                        color: '#000',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                        maxWidth: '100%',
                        margin: '0 auto',
                    }}
                >
                    <h2 style={{ fontFamily: 'GveretLevin', marginBottom: '20px', direction: 'rtl' }}>משקל</h2>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#000' }}>
                        {weight} <span style={{ fontSize: '18px', color: '#000' }}>kg</span>
                    </div>
                    <div
                        style={{
                            width: '100%',
                            margin: '20px 0',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {renderScale()}
                    </div>

                </div>
            </Box>

            <Modal
                open={isHeightModalOpen}
                onClose={() => setIsHeightModalOpen(false)}
                aria-labelledby="height-modal-title"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        justifyContent: 'space-between',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '90vw',
                        bgcolor: '#fff',
                        borderRadius: '8px',
                        boxShadow: 24,
                        p: 4,
                        direction: 'rtl',
                        textAlign: 'right',
                    }}
                >
                    <Typography
                        style={{
                            textAlign: 'right',
                            fontSize: '24px',
                            color: '#343a40',
                            direction: 'rtl',
                            marginLeft: '10px',
                            marginBottom: 0,
                            fontFamily: 'GveretLevin',
                        }}
                    >
                        הזן גובה
                    </Typography>
                    <TextField
                        type="number"
                        textAlign="center"
                        value={height || ''}
                        onChange={(e) => setHeight(Number(e.target.value))}
                        label="ס״מ"
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            textAlign: 'center',
                        }}
                    />

                </Box>
            </Modal>

            {/*BMI*/}
            <Box style={{
                margin: '20px', width: '50%', display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                borderRadius: '10px',
                color: '#000',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                maxWidth: '100%',
            }}>

                {/* Existing BMI display and save button */}
                <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '20px' }}>
                    <h4>BMI: {bmi}</h4>
                    <p
                        style={{
                            color: bmi < 18.5 ? '#f1c40f' : bmi < 24.9 ? '#2ecc71' : bmi < 29.9 ? '#e67e22' : '#e74c3c',
                        }}
                    >
                        {bmi && `${bmiStatus}.`}
                    </p>
                </div>
            </Box>



            <button
                onClick={handleSave}
                style={{
                    marginBottom: '20px',
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#B5A78A',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontFamily: 'GveretLevin'
                }}
            >
                שמירת המשקל
            </button>



            <div style={{ marginBottom: '20px' }}>
                <span
                    onClick={() => setTimeFrame('daily')} // Change 'weekly' to 'daily'
                    style={timeFrame === 'daily' ? activeStyle : inactiveStyle}
                >
                    יומי
                </span>
                <span
                    onClick={() => setTimeFrame('monthly')}
                    style={timeFrame === 'monthly' ? activeStyle : inactiveStyle}
                >
                    חודשי
                </span>
                <span
                    onClick={() => setTimeFrame('yearly')}
                    style={timeFrame === 'yearly' ? activeStyle : inactiveStyle}
                >
                    שנתי
                </span>
            </div>


            <Line data={data} />

            {
                showPopup && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setShowPopup(false)}
                    >
                        <div
                            style={{
                                backgroundColor: '#FFF',
                                padding: '40px',
                                borderRadius: '10px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                maxWidth: '400px',
                                width: '70%'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <span style={{ fontSize: '48px' }}>👍</span>
                            </div>
                            <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                                !המשקל עודכן
                            </p>

                            <button
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#B5A78A',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => setShowPopup(false)}
                            >
                                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>!</span>
                                <MonetizationOnIcon sx={{ fontSize: '20px', color: '#FFD700' }} />
                                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>3 קיבלת</span>
                            </button>
                        </div>
                    </div>
                )
            }

        </div >
    );
};

export default WeightTracker;
